/**
 * Follow-the-thumb sidebar drag, ported from the original's `sbDrag`.
 *
 * A pure function of finger X — no once-per-gesture latch — so dragging right reveals the
 * rail and reversing mid-drag backs it off, rather than the gesture committing on the
 * first few pixels. That reversibility is the point.
 *
 * Release snaps: a flick decides by its SIGN (velocity past FLING px/ms), otherwise it
 * lands in whichever half it was released in. `body.sb-dragging` kills the CSS transition
 * during the drag so the panel stays glued to the thumb, and removing it lets the same
 * transition animate the snap.
 */

const FLING = 0.5 // px/ms release speed that snaps regardless of position
/**
 * Travel that counts as a deliberate swipe rather than a wobble.
 *
 * Release commits on DIRECTION, not on distance: a short drag right opens the rail
 * fully. It used to need half the panel's width — 104px of 208 — which meant a normal
 * flick started the reveal and then dropped it back, reading as the gesture being
 * ignored. Direction keeps the reversal the follow-the-thumb drag exists for: pull
 * right and push back past where you started and it still closes.
 *
 * Below this, the finger has not really moved, so the state is left alone rather than
 * toggled by a stray touch.
 */
const COMMIT_PX = 8
const DEFAULT_W = 208

type Commit = (open: boolean) => void

let active = false
let startX = 0
let startOffset = 0
let curOffset = 0
let lastX = 0
let lastT = 0
let vel = 0
let panelW = DEFAULT_W
let dragNarrow = false
/** Rail state when this gesture began — decides what a leftward swipe MEANS. */
let wasOpenAtStart = false
let commit: Commit = () => {}
let isOpenNow: () => boolean = () => false

function el(): HTMLElement | null {
  return document.getElementById('agentsSidebar')
}
function backdrop(): HTMLElement | null {
  return document.getElementById('sbBackdrop')
}

/**
 * A leftward swipe while the rail is CLOSED, which this drag has nothing to do
 * with — the offset is already clamped shut, so the gesture currently does
 * nothing at all. That makes it free, and the owner asked for it to open the
 * note panel.
 *
 * It lives here rather than in a second listener because both gestures start
 * identically: only the rail's state decides which one the user meant. Two
 * independent handlers would both fire, and closing the rail would also open
 * the panel.
 */
let onLeftSwipe: (() => void) | null = null
/** Whether THIS gesture began near the note handle — the only place a leftward
 *  swipe may open the panel (owner, 2026-08-08: 將範圍縮小到只有在 bar 附近才生效). */
let noteEligible = false

/**
 * Did a touch start near the note handle (the right-edge grip bar)?
 *
 * "Near", not "on": the grip is deliberately slim, and demanding a hit on its
 * exact box would make the gesture feel broken. 48px of tolerance around it —
 * a finger-width — is the whole meaning of 附近. With the handle absent (note
 * already open, or no agent selected) nothing is near it.
 */
export function nearNoteHandle(x: number, y: number): boolean {
  const h = document.getElementById('noteHandle')
  if (!h) return false
  const r = h.getBoundingClientRect()
  const PAD = 48
  return x >= r.left - PAD && x <= r.right + PAD && y >= r.top - PAD && y <= r.bottom + PAD
}

/** Wire the drag to React state. Called once by App. */
export function configureSbDrag(opts: {
  isOpen: () => boolean
  onCommit: Commit
  onLeftSwipeClosed?: () => void
}) {
  isOpenNow = opts.isOpen
  commit = opts.onCommit
  onLeftSwipe = opts.onLeftSwipeClosed ?? null
}

function panelWidth(): number {
  const e = el()
  if (!e) return DEFAULT_W
  return e.offsetWidth || Number.parseFloat(getComputedStyle(e).width) || DEFAULT_W
}

function setOffset(px: number) {
  const e = el()
  if (!e) return
  // Clamp to [closed, open].
  curOffset = Math.max(-panelW, Math.min(0, px))
  e.style.transform = `translateX(${curOffset}px)`
  if (dragNarrow) {
    // Fade the drawer backdrop in step with the reveal.
    const revealed = (curOffset + panelW) / panelW
    const b = backdrop()
    if (b) {
      b.classList.add('show')
      b.style.opacity = String(revealed)
    }
  }
}

export const sbDrag = {
  isActive: () => active,

  begin(x: number, fromNoteBar = false) {
    if (!el()) return
    active = true
    noteEligible = fromNoteBar
    panelW = panelWidth()
    dragNarrow = window.innerWidth < 900
    startX = lastX = x
    lastT = performance.now()
    vel = 0
    wasOpenAtStart = isOpenNow()
    // Anchor at the current committed state, not at zero.
    startOffset = wasOpenAtStart ? 0 : -panelW
    document.body.classList.add('sb-dragging')
    setOffset(startOffset)
  },

  move(x: number) {
    if (!active) return
    const now = performance.now()
    const dt = now - lastT
    if (dt > 0) {
      vel = (x - lastX) / dt
      lastX = x
      lastT = now
    }
    // 1:1 with finger travel from the touch origin.
    setOffset(startOffset + (x - startX))
  },

  end(): boolean {
    if (!active) return false
    active = false
    const moved = curOffset - startOffset
    // Travel measured from the finger, not from the (clamped) panel offset: with
    // the rail already shut, dragging further left moves the panel zero pixels,
    // so `moved` is 0 and the gesture would be invisible here.
    const fingerMoved = lastX - startX
    const open =
      Math.abs(vel) > FLING
        ? vel > 0
        : moved > COMMIT_PX
          ? true
          : moved < -COMMIT_PX
            ? false
            : isOpenNow()

    document.body.classList.remove('sb-dragging')
    // Hand the transform back to the CSS class so the snap animates.
    const e = el()
    if (e) e.style.transform = ''
    const b = backdrop()
    if (b) {
      b.classList.remove('show')
      b.style.opacity = ''
    }

    /*
     * A deliberate leftward swipe with the rail already closed is not a rail
     * gesture at all — nothing moved, and nothing was going to. That is the
     * opening the note panel uses — but ONLY when the gesture began near the
     * note bar (owner, 2026-08-08: 手機版面上從右至左滑開啟 Note 的動作，將
     * 範圍縮小到只有在 bar 附近才生效). A whole-surface trigger kept firing
     * from ordinary horizontal wobble anywhere on screen; the bar's
     * neighbourhood is what the thumb actually means by "pull this out".
     */
    if (!wasOpenAtStart && noteEligible && fingerMoved < -COMMIT_PX && onLeftSwipe) {
      onLeftSwipe()
      commit(false)
      return false
    }

    commit(open)
    return open
  },
}
